import {
    Box,
    Button,
    Divider,
    FormControlLabel,
    IconButton,
    Typography,
} from '@mui/material';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import CommonInput from '../../component/form/common-input.component';
import { IOSSwitch } from '../../component/switch/IOSSwitch';
import i18n from '../../i18n';
import type { JobSettingsEmployeeRow, JobSettingsExceptionItem } from './job-settings.types';
import { ReactComponent as SaveIcon } from '../../static/images/svg/ic_round-save.svg';
import { ReactComponent as PlusIcon } from '../../static/images/svg/ic_round-plus.svg';
import { ReactComponent as EpSuccessFilledIcon } from '../../static/images/svg/ep_success-filled.svg';
import { ReactComponent as DeleteForeverIcon } from '../../static/images/svg/ic_baseline-delete-forever.svg';
import { ReactComponent as MinusIcon } from '../../static/images/svg/ic_round-minus.svg';
import { ReactComponent as CheckCircleIcon } from '../../static/images/svg/material-symbols_check-circle-outline-rounded.svg';
/** Tabler Icons：chevron-down（tabler:chevron-down） */
import { ReactComponent as TablerChevronDownIcon } from '../../static/images/svg/tabler_chevron-down.svg';
import './scss/job-settings-detail-panel.style.scss';

export type JobSettingsEffectivePermissionChip = {
    key: string;
    labelKey: string;
};

export type JobSettingsDetailPanelProps = {
    /** 岗位下拉选项（来自 `get_employee_permission_setting`，与 RBP 员工等级一致） */
    roleSelectItems: { value: string; label: string }[];
    /** 最终生效权限中已开启项（Chip 展示） */
    effectivePermissionChips: JobSettingsEffectivePermissionChip[];
    /** 当前选中的员工行 */
    selected: JobSettingsEmployeeRow;
    /** 岗位下拉当前值 */
    roleValue: string;
    /** 岗位变更 */
    onRoleChange: (value: string) => void;
    /** 个人权限补充开关 */
    personalSupplement: boolean;
    /** 个人权限补充变更 */
    onPersonalSupplementChange: (value: boolean) => void;
    /** 例外项列表（仅个人权限补充开启时展示） */
    exceptions: JobSettingsExceptionItem[];
    /** 删除一条例外 */
    onRemoveException: (id: string) => void;
    /** 打开新增/编辑例外权限弹窗 */
    onAddException: () => void;
    /** 删除账户 */
    onDeleteAccount: () => void;
    /** 取消编辑，恢复进入本行时的草稿 */
    onCancel: () => void;
    /** 保存请求进行中（底部按钮 loading） */
    saveSubmitting: boolean;
    /** 保存 */
    onSave: () => void;
};

/**
 * 岗位设置右侧详情区：当前员工权限、可选例外项、当前岗位权限与底部操作（Figma 7714:110277）
 */
export default function JobSettingsDetailPanel(props: JobSettingsDetailPanelProps) {
    const {
        roleSelectItems,
        effectivePermissionChips,
        selected,
        roleValue,
        onRoleChange,
        personalSupplement,
        onPersonalSupplementChange,
        exceptions,
        onRemoveException,
        onAddException,
        onDeleteAccount,
        onCancel,
        saveSubmitting,
        onSave,
    } = props;

    return (
        <Box className="job-settings-detail-panel">
            <Box className="job-settings-detail-panel__scroll">
                <Box className="job-settings-detail-panel__header">
                    <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: '24px', color: 'var(--text-dark)' }}>
                        {selected.displayName}
                    </Typography>
                    <Button
                        type="button"
                        variant="text"
                        disabled={saveSubmitting}
                        onClick={onDeleteAccount}
                        startIcon={<DeleteForeverIcon width={24} height={24} style={{ color: 'var(--func-error)' }} aria-hidden />}
                        sx={{
                            color: 'var(--func-error)',
                            fontWeight: 700,
                            fontSize: 20,
                            textTransform: 'none',
                            minWidth: 0,
                        }}
                    >
                        {i18n.t('job_settings_delete_account')}
                    </Button>
                </Box>
                <Divider className="job-settings-detail-panel__divider" />

                <Typography className="job-settings-detail-panel__section-title">
                    {i18n.t('job_settings_section_current_employee_permission')}
                </Typography>

                <Box className="job-settings-detail-panel__role-row">
                    <CommonInput
                        inputType="SELECT_PILL"
                        fullWidth
                        size="small"
                        label={undefined}
                        hiddenLabel
                        value={roleValue}
                        items={roleSelectItems}
                        onChange={(e) => onRoleChange(String((e.target as HTMLInputElement).value))}
                        className="job-settings-detail-panel__role-select"
                        pillDropdownIcon={TablerChevronDownIcon}
                        sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}
                    />
                    <Box className="job-settings-detail-panel__personal-toggle">
                        <Box className="job-settings-detail-panel__personal-toggle-text">
                            <Typography className="job-settings-detail-panel__personal-toggle-label">
                                {i18n.t('job_settings_personal_permission_toggle_label')}
                            </Typography>
                            <Typography className="job-settings-detail-panel__personal-toggle-sub">
                                {i18n.t('job_settings_personal_permission_toggle_sub')}
                            </Typography>
                        </Box>
                        <FormControlLabel
                            label=""
                            control={(
                                <IOSSwitch
                                    checked={personalSupplement}
                                    onChange={(_, v) => onPersonalSupplementChange(v)}
                                    inputProps={{ 'aria-label': i18n.t('job_settings_personal_permission_toggle_label') }}
                                />
                            )}
                            sx={{ m: 0, flexShrink: 0 }}
                        />
                    </Box>
                </Box>

                {personalSupplement ? (
                    <Box className="job-settings-detail-panel__exception-section">
                        <Typography className="job-settings-detail-panel__section-title">
                            {i18n.t('job_settings_section_exception_list')}
                        </Typography>

                        <Box className="job-settings-detail-panel__exceptions-scroll">
                            {exceptions.map((ex) => (
                                <Box
                                    key={ex.id}
                                    className={
                                        ex.kind === 'add'
                                            ? 'job-settings-detail-panel__exception-row job-settings-detail-panel__exception-row--add'
                                            : 'job-settings-detail-panel__exception-row job-settings-detail-panel__exception-row--subtract'
                                    }
                                >
                                    <Box
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: ex.kind === 'add' ? 0 : '24px',
                                            bgcolor: ex.kind === 'add' ? 'transparent' : 'var(--func-sp1)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {ex.kind === 'add' ? (
                                            <EpSuccessFilledIcon width={24} height={24} aria-hidden />
                                        ) : (
                                            <MinusIcon width={20} height={20} style={{ color: 'var(--icon-neutral-100)' }} aria-hidden />
                                        )}
                                    </Box>
                                    <Box sx={{ flex: '1 1 160px', minWidth: 0, textAlign: 'left' }}>
                                        <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: '24px', color: 'var(--text-dark)' }}>
                                            {i18n.t(ex.titleI18nKey)}
                                        </Typography>
                                        <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: '24px', color: 'var(--text-secondary)' }}>
                                            {i18n.t(ex.descI18nKey)}
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        type="button"
                                        onClick={() => onRemoveException(ex.id)}
                                        aria-label={i18n.t('job_settings_remove_exception')}
                                        size="small"
                                        sx={{ color: 'var(--func-error)' }}
                                    >
                                        <DeleteForeverIcon width={32} height={32} />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>

                        <Button
                            type="button"
                            variant="outlined"
                            onClick={onAddException}
                            fullWidth
                            className="job-settings-detail-panel__add-exception-btn"
                            startIcon={<PlusIcon width={28} height={28} style={{ color: 'var(--text-dark)' }} aria-hidden />}
                        >
                            {i18n.t('job_settings_add_more_exceptions')}
                        </Button>
                    </Box>
                ) : null}

                <Box className="job-settings-detail-panel__permission-block">
                    <Typography
                        className="job-settings-detail-panel__section-title job-settings-detail-panel__section-title--perm"
                    >
                        {i18n.t('job_settings_section_current_role_permission')}
                    </Typography>

                    <Box className="job-settings-detail-panel__chips-scroll">
                        {effectivePermissionChips.length === 0 ? (
                            <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: '24px', color: 'var(--text-secondary)', px: 1, py: 0.5 }}>
                                {i18n.t('general_text_no_data')}
                            </Typography>
                        ) : (
                            effectivePermissionChips.map((d) => (
                                <Box key={d.key} className="job-settings-detail-panel__chip">
                                    <CheckCircleIcon width={24} height={24} style={{ color: 'var(--func-success)' }} aria-hidden />
                                    <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: '24px', color: 'var(--text-dark)', whiteSpace: 'nowrap' }}>
                                        {i18n.t(d.labelKey)}
                                    </Typography>
                                </Box>
                            ))
                        )}
                    </Box>
                </Box>
            </Box>

            <Box className="job-settings-detail-panel__footer">
                <Button
                    type="button"
                    variant="text"
                    disabled={saveSubmitting}
                    onClick={onCancel}
                    sx={{
                        flex: 1,
                        minHeight: 64,
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontSize: 20,
                        fontWeight: 700,
                        color: 'var(--text-secondary)',
                        bgcolor: 'var(--icon-neutral-200)',
                        border: '1px solid',
                        borderColor: 'var(--stroke-neutral-800-a25)',
                    }}
                >
                    {i18n.t('job_settings_cancel')}
                </Button>
                <LoadingOverlayButton
                    type="button"
                    color="inherit"
                    loading={saveSubmitting}
                    loadingIndicatorSize={22}
                    onClick={onSave}
                    sx={{
                        flex: 1,
                        minHeight: 64,
                        borderRadius: '8px',
                        textTransform: 'none',
                        bgcolor: 'var(--fill-primary-300)',
                        boxShadow: 'none',
                        color: 'var(--text-light)',
                        gap: 1,
                        '&:hover': { bgcolor: 'var(--fill-primary-300)', boxShadow: 'none', opacity: 0.92 },
                        '& .MuiButton-startIcon': {
                            color: 'var(--text-light)',
                        },
                    }}
                    startIcon={<SaveIcon width={24} height={24} aria-hidden />}
                >
                    <Typography component="span" sx={{ fontSize: 20, fontWeight: 700, lineHeight: '24px', color: 'var(--text-light)' }}>
                        {i18n.t('job_settings_save')}
                    </Typography>
                </LoadingOverlayButton>
            </Box>
        </Box>
    );
}
